import React, { useEffect, useRef, useState } from 'react'
import { ReactModal } from '../../../../../components'
import { IMG } from '../../../../../themes'
import { ATTENDEE_CHECK_IN, MODAL_CONFIRM } from '../../../../../helpers'
import { AttendeeCheckInModalStyled } from './styles'
import { getGuestLabel, isDeclined, isPendingCheckIn } from './checkIn'

const FILTER = {
  ALL: 'all',
  NOT_CHECKED_IN: 'notCheckedIn',
  CHECKED_IN: 'checkedIn'
}

const getSortRank = (guest) => {
  if (isDeclined(guest)) return 2
  return guest.isCheckedIn ? 1 : 0
}

const sortGuests = (guests) => [...guests].sort((a, b) => {
  const rank = getSortRank(a) - getSortRank(b)
  if (rank !== 0) return rank
  return Number(b.ownership == 1) - Number(a.ownership == 1)
})

const getInitials = (label) => label.trim().split(/\s+/).slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase()

const COPIED_MS = 1500
const GUEST_CONFIRM_RESET_MS = 4000

const PinCopyButton = ({ pin, language }) => {
  const [isCopied, setIsCopied] = useState(false)
  const timerRef = useRef()

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const handleCopy = async () => {
    await navigator.clipboard.writeText(pin)
    setIsCopied(true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setIsCopied(false), COPIED_MS)
  }

  return (
    <button
      className={`attendee-copy${isCopied ? ' copied' : ''}`}
      title={isCopied ? ATTENDEE_CHECK_IN.PIN_COPIED[language] : ATTENDEE_CHECK_IN.COPY_PIN[language]}
      onClick={handleCopy}
    >
      <img src={IMG[isCopied ? 'ic_copied.svg' : 'ic_copy.svg']} alt='' />
    </button>
  )
}

const AttendeeCheckInModal = ({
  isVisible,
  guests = [],
  isCheckInDisabled,
  checkInWindowText,
  colorTheme,
  onCheckInAll,
  onCheckInGuest,
  onClose,
  language = 'EN'
}) => {
  const [keyword, setKeyword] = useState('')
  const [filter, setFilter] = useState(FILTER.ALL)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [confirmingPin, setConfirmingPin] = useState(null)
  const [checkingInPins, setCheckingInPins] = useState([])

  useEffect(() => {
    if (!confirmingPin) return undefined
    const timer = setTimeout(() => setConfirmingPin(null), GUEST_CONFIRM_RESET_MS)
    return () => clearTimeout(timer)
  }, [confirmingPin])

  const checkedInCount = guests.filter(guest => guest.isCheckedIn).length
  const declinedCount = guests.filter(isDeclined).length
  const pendingCount = guests.filter(isPendingCheckIn).length
  const expectedCount = guests.length - declinedCount
  const isComplete = pendingCount === 0
  const progressPercent = expectedCount > 0 ? Math.round((checkedInCount / expectedCount) * 100) : 0
  const isOutsideCheckInTime = isCheckInDisabled && !isComplete

  const searchText = keyword.trim().toLowerCase()
  const displayGuests = sortGuests(guests).filter((guest) => {
    const isMatchKeyword = [guest.name, guest.username, guest.pincode].some(value => String(value || '').toLowerCase().includes(searchText))
    const isMatchFilter = filter === FILTER.ALL || (filter === FILTER.CHECKED_IN) === guest.isCheckedIn
    return isMatchKeyword && isMatchFilter
  })

  const filterOptions = [
    { key: FILTER.ALL, label: ATTENDEE_CHECK_IN.FILTER_ALL[language] },
    { key: FILTER.NOT_CHECKED_IN, label: ATTENDEE_CHECK_IN.NOT_CHECKED_IN[language] },
    { key: FILTER.CHECKED_IN, label: ATTENDEE_CHECK_IN.CHECKED_IN[language] }
  ]

  const handleConfirmCheckInAll = async () => {
    setIsSubmitting(true)
    await onCheckInAll()
    setIsSubmitting(false)
    setIsConfirming(false)
  }

  const handleCheckInGuest = async (guest) => {
    setConfirmingPin(null)
    setCheckingInPins(prev => [...prev, guest.pincode])
    await onCheckInGuest(guest)
    setCheckingInPins(prev => prev.filter(pin => pin !== guest.pincode))
  }

  const handleModalMouseDown = (e) => {
    if (confirmingPin && !e.target.closest('.attendee-guest-confirm')) setConfirmingPin(null)
  }

  const renderStatus = (guest) => {
    if (isDeclined(guest)) return ATTENDEE_CHECK_IN.DECLINED[language]
    if (checkingInPins.includes(guest.pincode)) return ATTENDEE_CHECK_IN.CHECKING_IN[language]
    if (confirmingPin === guest.pincode) return ATTENDEE_CHECK_IN.CLICK_AGAIN_TO_CONFIRM[language]
    if (!guest.isCheckedIn) return ATTENDEE_CHECK_IN.NOT_CHECKED_IN[language]

    return <span className='attendee-status-done'>✓ {ATTENDEE_CHECK_IN.CHECKED_IN[language]}</span>
  }

  const renderGuestAction = (guest) => {
    if (!isPendingCheckIn(guest) || !guest.pincode || checkingInPins.includes(guest.pincode)) return null

    if (confirmingPin === guest.pincode) {
      return (
        <button className='attendee-guest-button attendee-guest-confirm' onClick={() => handleCheckInGuest(guest)}>
          {ATTENDEE_CHECK_IN.CONFIRM_CHECK_IN_GUEST[language]}
        </button>
      )
    }

    return (
      <button
        className='attendee-guest-button'
        disabled={isCheckInDisabled}
        onClick={() => setConfirmingPin(guest.pincode)}
      >
        {ATTENDEE_CHECK_IN.CHECK_IN_GUEST[language]}
      </button>
    )
  }

  return (
    <ReactModal
      isVisible={isVisible}
      width='min(800px, calc(100vw - 48px))'
      borderRadius='16px'
      onRequestClose={onClose}
    >
      <AttendeeCheckInModalStyled colorTheme={colorTheme} onMouseDown={handleModalMouseDown}>
        <div className='attendee-header'>
          <label className='attendee-title'>{ATTENDEE_CHECK_IN.TITLE[language]}</label>
          <img
            className='attendee-close'
            src={IMG['icon_close.svg']}
            onClick={onClose}
            alt='close'
          />
        </div>

        <div className={`attendee-hero${isComplete ? ' complete' : ''}`}>
          {isConfirming ? (
            <div className='attendee-confirm'>
              <div>
                <strong>{ATTENDEE_CHECK_IN.CHECK_IN_ALL_CONFIRM_TITLE[language]}</strong>
                <p>{ATTENDEE_CHECK_IN.CHECK_IN_ALL_CONFIRM_MSG[language](pendingCount)}</p>
              </div>
              <div className='attendee-confirm-actions'>
                <button className='attendee-button' disabled={isSubmitting} onClick={() => setIsConfirming(false)}>
                  {MODAL_CONFIRM.KEEP[language]}
                </button>
                <button className='attendee-button primary' disabled={isSubmitting} onClick={handleConfirmCheckInAll}>
                  {MODAL_CONFIRM.HEAD[language]}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className='attendee-progress'>
                <div className='attendee-count'>
                  <b>{checkedInCount}</b>
                  <span>/ {expectedCount}</span>
                </div>
                <div className='attendee-meter-wrap'>
                  <div className='attendee-meter-label'>
                    {isComplete ? `✓ ${ATTENDEE_CHECK_IN.ALL_CHECKED_IN[language]}` : ATTENDEE_CHECK_IN.CHECKED_IN[language]}
                    {declinedCount > 0 && <small> · {ATTENDEE_CHECK_IN.DECLINED_COUNT[language](declinedCount)}</small>}
                  </div>
                  <div className='attendee-meter'><div style={{ width: `${progressPercent}%` }} /></div>
                  {isOutsideCheckInTime && (
                    <div className='attendee-window'>
                      {checkInWindowText
                        ? ATTENDEE_CHECK_IN.CHECK_IN_WINDOW[language](checkInWindowText)
                        : ATTENDEE_CHECK_IN.OUTSIDE_TIME[language]}
                    </div>
                  )}
                </div>
              </div>
              {!isComplete && (
                <button
                  className='attendee-button primary large'
                  disabled={isCheckInDisabled}
                  onClick={() => setIsConfirming(true)}
                >
                  {ATTENDEE_CHECK_IN.CHECK_IN_ALL[language]}
                  <span className='attendee-button-count'>{pendingCount}</span>
                </button>
              )}
            </>
          )}
        </div>

        <div className='attendee-tools'>
          <input
            type='text'
            className='attendee-search'
            placeholder={ATTENDEE_CHECK_IN.SEARCH_NAME_EMAIL_OR_PIN[language]}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <div className='attendee-filters'>
            {filterOptions.map(option => (
              <button
                key={option.key}
                className={`attendee-filter${filter === option.key ? ' active' : ''}`}
                onClick={() => setFilter(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className='attendee-body'>
          {displayGuests.length === 0 && (
            <div className='attendee-empty'>{ATTENDEE_CHECK_IN.NO_MATCHING_GUEST[language]}</div>
          )}
          {displayGuests.map((guest, index) => {
            const hasPin = isPendingCheckIn(guest)
            const stateClass = isDeclined(guest) ? ' declined' : guest.isCheckedIn ? ' checked-in' : ''
            const guestLabel = getGuestLabel(guest)
            return (
              <div key={guest.id || guest.pincode || index} className={`attendee-row${stateClass}`}>
                <span className='attendee-avatar'>
                  {guest.profileThumbnail
                    ? <img src={`data:image/png;base64,${guest.profileThumbnail}`} alt={guestLabel} />
                    : getInitials(guestLabel)}
                </span>
                <span className='attendee-name' title={guestLabel}>
                  {guestLabel}
                  {guest.ownership == 1 && <span className='attendee-owner'>{ATTENDEE_CHECK_IN.OWNER[language]}</span>}
                </span>
                <span className='attendee-pin'>
                  {hasPin ? guest.pincode || '-' : ''}
                  {hasPin && guest.pincode && <PinCopyButton pin={guest.pincode} language={language} />}
                </span>
                <span className='attendee-status'>
                  {renderStatus(guest)}
                  {renderGuestAction(guest)}
                </span>
              </div>
            )
          })}
        </div>
      </AttendeeCheckInModalStyled>
    </ReactModal>
  )
}

export default AttendeeCheckInModal
